import { useState, useEffect, useCallback } from 'react';
import axiosInstance, { endpoints } from 'src/utils/axios';
import SvgColor from 'src/components/svg-color';
import Iconify from 'src/components/iconify';


// ----------------------------------------------------------------------

// Icon helper - renders navbar SVG icons
const svgIcon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

/**
 * Determine if icon is a navbar SVG icon or an Iconify icon
 */
const isNavbarIcon = (iconName) => {
  if (!iconName) return false;
  return iconName.startsWith('ic_') || iconName === 'settings';
};

/**
 * Get icon component from icon name string
 */
const getIcon = (iconName) => {
  if (!iconName) {
    return svgIcon('ic_blank');
  }
  if (isNavbarIcon(iconName)) {
    return svgIcon(iconName);
  }
  return <Iconify icon={iconName} sx={{ width: 1, height: 1 }} />;
};

// ----------------------------------------------------------------------

/**
 * Hook to fetch and transform dynamic navigation data from backend
 * @returns {Object} { navData, loading, error, refetch }
 */
export function useDynamicNavData() {
  const [navData, setNavData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNavigation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(endpoints.navigation.menu);

      if (response.data?.success && response.data?.data) {
        // Transform API response to nav format
        const transformedData = response.data.data.map((module) => ({
          subheader: module.subheader,
          items: module.items.map((menu) => {
            const menuItem = {
              title: menu.title,
              path: menu.path,
              icon: getIcon(menu.icon),
            };

            // Add children only if present and not a direct link
            if (menu.children && menu.children.length > 0 && !menu.is_direct_link) {
              menuItem.children = menu.children.map((submenu) => ({
                title: submenu.title,
                path: submenu.path,
              }));
            }

            return menuItem;
          }),
        }));

        setNavData(transformedData);
      } else {
        throw new Error('Invalid API response');
      }
    } catch (err) {
      console.error('Failed to fetch navigation:', err);
      setError(err);
      setNavData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for manual refetch events (e.g., after role/plan changes)
  useEffect(() => {
    const handleRefetch = () => {
      console.log('🔄 Refetching navigation via event...');
      fetchNavigation();
    };

    window.addEventListener('refetch-navigation', handleRefetch);
    return () => window.removeEventListener('refetch-navigation', handleRefetch);
  }, [fetchNavigation]);

  // Fetch once on mount — backend uses JWT (req.user) for filtering,
  // so no need to re-fetch on client-side rights changes.
  useEffect(() => {
    fetchNavigation();
  }, [fetchNavigation]);

  return {
    navData,
    loading,
    error,
    refetch: fetchNavigation,
  };
}

export default useDynamicNavData;
